// client/src/dnd/emitter_tool/components/panels/EmitterPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";

const OPEN_KEY = "EmitterTool:EmitterPanelOpenV1";

const sectionHeader = { fontSize: 12, fontWeight: 600, opacity: 0.9, margin: "6px 0" };
const labelStyle = { fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" };
const selectStyle = {
  width: "100%",
  maxWidth: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
  boxSizing: "border-box",
  display: "block",
};

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }
function toNum(v, fallback = 0) { const n = Number(v); return Number.isFinite(n) ? n : fallback; }
const norm360 = (d)=>{ let x=d%360; if (x<0) x+=360; return x; };
const spanFromStartEnd = (s,e)=>{ let span=(e - s + 360) % 360; return span === 0 ? 360 : span; };

function emit(name, detail) { try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch {} }

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

function Slider({ label, value, onChange, min = 0, max = 10, step = 0.1, suffix = "" }) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : String(min));

  useEffect(() => {
    const asStr = String(Number.isFinite(value) ? value : min);
    if (asStr !== text) setText(asStr);
  }, [value, min, max, text]);

  const commitNumber = (raw) => {
    const parsed = toNum(raw, value ?? min);
    const clamped = clamp(parsed, min, max);
    onChange?.(clamped);
  };

  return (
    <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
      <div style={labelStyle}>{label}{suffix ? ` (${suffix})` : ""}</div>

      <input
        type="number"
        value={text}
        min={min}
        max={max}
        step={step}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => commitNumber(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        style={{
          width: "100%",
          background: "#0b0d12",
          color: "white",
          border: "1px solid #374151",
          borderRadius: 6,
          padding: "4px 6px",
          fontSize: 12,
          textAlign: "right",
          boxSizing: "border-box",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="range"
          value={Number.isFinite(value) ? value : min}
          min={min}
          max={max}
          step={step}
          onChange={(e) => { const v = toNum(e.target.value, min); onChange?.(v); }}
          style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", accentColor: "#64748b", cursor: "pointer" }}
        />
        {suffix ? <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{suffix}</span> : null}
      </div>
    </div>
  );
}

function LabeledSelect({ label, value, onChange, children }) {
  return (
    <div style={{ minWidth: 0, display: "grid", gap: 6 }}>
      <div style={labelStyle}>{label}</div>
      <select value={value} onChange={onChange} style={selectStyle}>
        {children}
      </select>
    </div>
  );
}

function ColorRow({ label, value, onChange }) {
  const toHex = (v) => {
    if (!v) return "#ffffff";
    if (typeof v === "string" && v.startsWith("#") && (v.length === 7 || v.length === 4)) return v;
    if (typeof v === "number") {
      const hex = "#" + (v >>> 0).toString(16).padStart(6, "0").slice(-6);
      return hex;
    }
    return "#ffffff";
  };
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ ...labelStyle }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="color"
          value={toHex(value)}
          onChange={(e) => onChange?.(e.target.value)}
          style={{ width: 36, height: 22, padding: 0, border: "1px solid #374151", borderRadius: 4, background: "#0b0d12" }}
        />
        <input
          type="text"
          value={toHex(value)}
          onChange={(e) => onChange?.(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 6,
            padding: "4px 6px",
            fontSize: 12,
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}

export default function EmitterPanel({
  emitterShape,
  setEmitterShape,
  shapeSettings = {},
  setShapeSettings,
  showShape,
  setShowShape,
  title = "Emitter",
  defaultOpen = true,
}) {
  const SHAPES = useMemo(
    () => [
      { key: "dot",     label: "Dot (point)" },
      { key: "circle",  label: "Circle (area)" },
      { key: "arc",     label: "Arc (circle edge)" },
      { key: "square",  label: "Square (area)" },
      { key: "triangle",label: "Triangle (isosceles)" },
      { key: "cone",    label: "Cone (directional)" },
      { key: "line",    label: "Line (segment)" },
      { key: "random",  label: "Full Container" },
      { key: "beam",    label: "Beam (laser/magic)" },
    ],
    []
  );

  const setSS = (patch) => {
    if (typeof setShapeSettings === "function") {
      setShapeSettings((cur) => {
        const base = cur || {};
        const next = typeof patch === "function" ? patch(base) : patch;
        return { ...base, ...next };
      });
    }
  };

  const normalizedSS = useMemo(
    () => ({
      pulseEnabled: false,
      spawnFromOrigin:
        shapeSettings?.spawnFromOrigin ?? (String(shapeSettings?.spawnMode || "shape").toLowerCase() === "origin"),
      spawnMode: String(shapeSettings?.spawnMode || ((shapeSettings?.spawnFromOrigin ? "origin" : "shape"))).toLowerCase(),
      feederCount: 3,
      feederRadiusM: 0.3,
      feederJitterPct: 25,
      beamEnabled: true,
      beamBaseWidthPx: 24,
      beamMaxLengthPx: 600,
      beamCoreColor: "#ffffff",
      beamGlowColor: "#66ccff",
      beamCoreAlpha: 1,
      beamGlowAlpha: 0.5,
      beamHardCap: false,
      beamCollideWithWalls: true,
      beamPulseSpeed: 0.8,
      beamNoise: 0.2,
      beamNoiseSpeed: 1.0,
      beamTileW: 128,
      beamAngleDeg: 0,
      startDeg: 0,
      endDeg: 90,
      arcSweepDeg: (() => {
        const s = Number(shapeSettings?.startDeg ?? 0);
        const e = Number(shapeSettings?.endDeg ?? 90);
        const span = spanFromStartEnd(norm360(s), norm360(e));
        return Number.isFinite(shapeSettings?.arcSweepDeg) ? shapeSettings.arcSweepDeg : span;
      })(),
      arcRotationDeg: (() => {
        const s = Number(shapeSettings?.startDeg ?? 0);
        return Number.isFinite(shapeSettings?.arcRotationDeg) ? shapeSettings.arcRotationDeg : s;
      })(),
      rotationAnimEnabled: !!shapeSettings?.rotationAnimEnabled,
      rotationAnimDir: shapeSettings?.rotationAnimDir === "cw" ? "cw" : "ccw",
      rotationAnimSpeedDegS: Number.isFinite(shapeSettings?.rotationAnimSpeedDegS) ? shapeSettings.rotationAnimSpeedDegS : 45,
      rotationAffectsParticles: shapeSettings?.rotationAffectsParticles ?? true,
      overlayCling: !!shapeSettings?.overlayCling,
      ...(shapeSettings || {}),
    }),
    [shapeSettings]
  );

  useEffect(() => {
    const needDefaults =
      !shapeSettings ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "pulseEnabled") ||
      (!Object.prototype.hasOwnProperty.call(shapeSettings, "spawnFromOrigin") &&
       !Object.prototype.hasOwnProperty.call(shapeSettings, "spawnMode")) ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "beamBaseWidthPx") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "startDeg") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "endDeg") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "arcSweepDeg") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "arcRotationDeg") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "rotationAnimEnabled") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "rotationAnimDir") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "rotationAnimSpeedDegS") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "rotationAffectsParticles") ||
      !Object.prototype.hasOwnProperty.call(shapeSettings, "overlayCling");
    if (needDefaults) {
      setSS({
        pulseEnabled: false,
        spawnFromOrigin: false,
        spawnMode: "shape",
        feederCount: 3,
        feederRadiusM: 0.3,
        feederJitterPct: 25,
        beamEnabled: true,
        beamBaseWidthPx: 24,
        beamMaxLengthPx: 600,
        beamCoreColor: "#ffffff",
        beamGlowColor: "#66ccff",
        beamCoreAlpha: 1,
        beamGlowAlpha: 0.5,
        beamHardCap: false,
        beamCollideWithWalls: true,
        beamPulseSpeed: 0.8,
        beamNoise: 0.2,
        beamNoiseSpeed: 1.0,
        beamTileW: 128,
        beamAngleDeg: 0,
        startDeg: 0,
        endDeg: 90,
        arcSweepDeg: 90,
        arcRotationDeg: 0,
        rotationAnimEnabled: false,
        rotationAnimDir: "ccw",
        rotationAnimSpeedDegS: 45,
        rotationAffectsParticles: true,
        overlayCling: false,
      });
    }
  }, [shapeSettings, setSS]);

  const {
    radius = 1,
    width = 2,
    height = 2,
    rotationDeg = 0,
    spreadDeg = 30,
    emissionMode = "area",
    edgeBehavior = "out",
    spawnFromOrigin = false,
    spawnMode = spawnFromOrigin ? "origin" : "shape",
    feederCount = 3,
    feederRadiusM = 0.3,
    feederJitterPct = 25,
    pulseEnabled = false,
    pulseMinPct = 1,
    pulseExpandTimeS = 1.5,
    pulseHoldAtMaxS = 0,
    pulseDelayAfterEmptyS = 0,
    pulsePingPong = false,
    beamEnabled = true,
    beamBaseWidthPx = 24,
    beamMaxLengthPx = 600,
    beamCoreColor = "#ffffff",
    beamGlowColor = "#66ccff",
    beamCoreAlpha = 1,
    beamGlowAlpha = 0.5,
    beamHardCap = false,
    beamCollideWithWalls = true,
    beamPulseSpeed = 0.8,
    beamNoise = 0.2,
    beamNoiseSpeed = 1.0,
    beamTileW = 128,
    beamAngleDeg = 0,
    startDeg = 0,
    endDeg = 90,
    arcSweepDeg = spanFromStartEnd(norm360(startDeg), norm360(endDeg)),
    arcRotationDeg = startDeg,
    rotationAnimEnabled = false,
    rotationAnimDir = "ccw",
    rotationAnimSpeedDegS = 45,
    rotationAffectsParticles = true,
    overlayCling = false,
  } = normalizedSS;

  const METERS_MIN = 0;
  const METERS_MAX = 30;
  const METERS_STEP_FINE = 0.05;
  const DEG_MIN = -360;
  const DEG_MAX = 360;
  const DEG_STEP = 1;
  const SPREAD_MIN = 0;
  const SPREAD_MAX = 180;

  const lastScaleRef = useRef({ square: 1, triangle: 1, cone: 1 });

  const [uiScaleSquare, setUiScaleSquare] = useState(1);
  const [uiScaleTriangle, setUiScaleTriangle] = useState(1);
  const [uiScaleCone, setUiScaleCone] = useState(1);

  useEffect(() => {
    if (emitterShape === "square") {
      setUiScaleSquare(1);
      lastScaleRef.current.square = 1;
    } else if (emitterShape === "triangle") {
      setUiScaleTriangle(1);
      lastScaleRef.current.triangle = 1;
    } else if (emitterShape === "cone") {
      setUiScaleCone(1);
      lastScaleRef.current.cone = 1;
    }
  }, [emitterShape]);

  const applyUniformScaleWH = (newScale, which) => {
    const prev = lastScaleRef.current[which] || 1;
    const ratio = (newScale || 1) / prev;
    if (!Number.isFinite(ratio) || ratio <= 0) return;

    setSS((cur) => {
      const curW = Number.isFinite(cur?.width) ? cur.width : width;
      const curH = Number.isFinite(cur?.height) ? cur.height : height;
      const nextW = Math.max(METERS_MIN, Math.min(METERS_MAX, curW * ratio));
      const nextH = Math.max(METERS_MIN, Math.min(METERS_MAX, curH * ratio));
      return { ...cur, width: nextW, height: nextH };
    });

    lastScaleRef.current[which] = newScale || 1;
  };

  const applyUniformScaleR = (newScale) => {
    const prev = lastScaleRef.current.cone || 1;
    const ratio = (newScale || 1) / prev;
    if (!Number.isFinite(ratio) || ratio <= 0) return;

    setSS((cur) => {
      const curR = Number.isFinite(cur?.radius) ? cur.radius : radius;
      const nextR = Math.max(METERS_MIN, Math.min(METERS_MAX, curR * ratio));
      return { ...cur, radius: nextR };
    });

    lastScaleRef.current.cone = newScale || 1;
  };

  useEffect(() => {
    emit("EmitterTool:UI:EmitterOverlayConfig", {
      show: !!showShape,
      shape: emitterShape || "dot",
      settings: normalizedSS,
    });
  }, [showShape, emitterShape, normalizedSS]);

  useEffect(() => {
    const onReq = () => {
      emit("EmitterTool:UI:EmitterOverlayConfig", {
        show: !!showShape,
        shape: emitterShape || "dot",
        settings: normalizedSS,
      });
    };
    window.addEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onReq);
    return () => window.removeEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onReq);
  }, [showShape, emitterShape, normalizedSS]);

  const renderSpawnSourceControls = (showFeederOptions) => {
    return (
      <>
        <div style={sectionHeader}>Spawn Source</div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={spawnMode === "origin"}
            onChange={(e) => {
              const on = e.target.checked;
              setSS({ spawnFromOrigin: on, spawnMode: on ? "origin" : "shape" });
            }}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Spawn from <b>origin</b> (emitter centre / cone tip). Unchecked = spawn in/along the shape.
          </span>
        </label>

        {showFeederOptions && spawnMode === "origin" && (
          <>
            <Slider
              label="Feeder count"
              value={feederCount}
              onChange={(v) => setSS({ feederCount: Math.round(clamp(v, 1, 8)) })}
              min={1}
              max={8}
              step={1}
            />
            <Slider
              label="Feeder radius"
              value={feederRadiusM}
              onChange={(v) => setSS({ feederRadiusM: Math.max(0, v) })}
              min={0}
              max={5}
              step={0.05}
              suffix="m"
            />
            <Slider
              label="Angle jitter"
              value={feederJitterPct}
              onChange={(v) => setSS({ feederJitterPct: clamp(v, 0, 100) })}
              min={0}
              max={100}
              step={1}
              suffix="%"
            />
          </>
        )}
      </>
    );
  };

  const renderEmissionControls = () => {
    if (!["circle", "square", "triangle", "arc"].includes(emitterShape)) return null;

    return (
      <>
        {emitterShape !== "arc" && (
          <>
            <div style={sectionHeader}>Emission</div>
            <LabeledSelect
              label="Emission Type"
              value={emissionMode}
              onChange={(e) => setSS({ emissionMode: e.target.value })}
            >
              <option value="area">Area-based</option>
              <option value="edge">Edge-based</option>
            </LabeledSelect>

            {emissionMode === "edge" && (
              <LabeledSelect
                label="Edge Behaviour"
                value={edgeBehavior}
                onChange={(e) => setSS({ edgeBehavior: e.target.value })}
              >
                <option value="pull">Pull to centre</option>
                <option value="out">Emit out</option>
                <option value="cling">Cling</option>
              </LabeledSelect>
            )}
          </>
        )}

        {renderSpawnSourceControls(true)}

        <div style={sectionHeader}>Pulse (single system)</div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={!!pulseEnabled}
            onChange={(e) => setSS({ pulseEnabled: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>Enable pulse scaling and cycle control</span>
        </label>

        <Slider
          label="Min size"
          value={Math.round(Math.max(0, Math.min(0.99, Number(pulseMinPct || 0))) * 100)}
          onChange={(v) => setSS({ pulseMinPct: Math.max(0, Math.min(0.99, v / 100)) })}
          min={0}
          max={99}
          step={1}
          suffix="%"
        />
        <Slider
          label="Expand time"
          value={pulseExpandTimeS}
          onChange={(v) => setSS({ pulseExpandTimeS: Math.max(0.05, v) })}
          min={0.05}
          max={60}
          step={0.01}
          suffix="s"
        />
        <Slider
          label="Hold at max"
          value={pulseHoldAtMaxS}
          onChange={(v) => setSS({ pulseHoldAtMaxS: Math.max(0, v) })}
          min={0}
          max={60}
          step={0.01}
          suffix="s"
        />
        <Slider
          label="Delay after empty"
          value={pulseDelayAfterEmptyS}
          onChange={(v) => setSS({ pulseDelayAfterEmptyS: Math.max(0, v) })}
          min={0}
          max={120}
          step={0.1}
          suffix="s"
        />

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={!!pulsePingPong}
            onChange={(e) => setSS({ pulsePingPong: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Ping-pong (continuous expand↔shrink; always emit)
          </span>
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <input
            type="checkbox"
            checked={!!overlayCling}
            onChange={(e) => setSS({ overlayCling: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Cling to overlay (LOS): place pulse along the overlay shape and hug walls/corners
          </span>
        </label>

        <div style={{ fontSize: 11, opacity: 0.7 }}>
          • Ping-pong OFF: emit during expand and hold; then stop, wait for particles to die, wait the delay, then repeat.{"  "}
          • Ping-pong ON: expand and contract repeatedly and keep emitting the whole time (no delay gating).{"  "}
          • Cling to overlay: particles follow the same line-of-sight clamp the cyan overlay uses.
        </div>
      </>
    );
  };

  const renderBeamControls = () => {
    if (emitterShape !== "beam") return null;

    return (
      <>
        <div style={sectionHeader}>Beam</div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={!!beamEnabled}
            onChange={(e) => setSS({ beamEnabled: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Enable beam (renders via BeamSystem instead of particle sprites)
          </span>
        </label>

        <Slider
          label="Width"
          value={beamBaseWidthPx}
          onChange={(v) => setSS({ beamBaseWidthPx: Math.round(clamp(v, 2, 512)) })}
          min={2}
          max={512}
          step={1}
          suffix="px"
        />
        <Slider
          label="Max length"
          value={beamMaxLengthPx}
          onChange={(v) => setSS({ beamMaxLengthPx: Math.round(clamp(v, 10, 5000)) })}
          min={10}
          max={5000}
          step={1}
          suffix="px"
        />
        <Slider
          label="Direction"
          value={beamAngleDeg}
          onChange={(v) => setSS({ beamAngleDeg: Math.round(clamp(v, DEG_MIN, DEG_MAX)) })}
          min={DEG_MIN}
          max={DEG_MAX}
          step={1}
          suffix="°"
        />
        <Slider
          label="Tile width"
          value={beamTileW}
          onChange={(v) => setSS({ beamTileW: Math.round(clamp(v, 16, 1024)) })}
          min={16}
          max={1024}
          step={1}
          suffix="px"
        />
        <Slider
          label="Pulse speed"
          value={beamPulseSpeed}
          onChange={(v) => setSS({ beamPulseSpeed: clamp(v, 0, 10) })}
          min={0}
          max={10}
          step={0.01}
          suffix="Hz"
        />
        <Slider
          label="Width noise"
          value={beamNoise}
          onChange={(v) => setSS({ beamNoise: clamp(v, 0, 1) })}
          min={0}
          max={1}
          step={0.01}
        />
        <Slider
          label="Noise speed"
          value={beamNoiseSpeed}
          onChange={(v) => setSS({ beamNoiseSpeed: clamp(v, 0, 10) })}
          min={0}
          max={10}
          step={0.01}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "10px 0" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!beamCollideWithWalls}
              onChange={(e) => setSS({ beamCollideWithWalls: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Shorten at walls</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!beamHardCap}
              onChange={(e) => setSS({ beamHardCap: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>Hard core</span>
          </label>
        </div>

        <ColorRow
          label="Core color"
          value={beamCoreColor}
          onChange={(v) => setSS({ beamCoreColor: v })}
        />
        <ColorRow
          label="Glow color"
          value={beamGlowColor}
          onChange={(v) => setSS({ beamGlowColor: v })}
        />
        <Slider
          label="Core alpha"
          value={beamCoreAlpha}
          onChange={(v) => setSS({ beamCoreAlpha: clamp(v, 0, 1) })}
          min={0}
          max={1}
          step={0.01}
        />
        <Slider
          label="Glow alpha"
          value={beamGlowAlpha}
          onChange={(v) => setSS({ beamGlowAlpha: clamp(v, 0, 1) })}
          min={0}
          max={1}
          step={0.01}
        />

        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
          Beam is rendered by a procedural texture + SimpleRope. Angle and length are applied at runtime; if “Shorten at walls” is on, the beam raycasts and stops at the first hit.
        </div>
      </>
    );
  };

  const updateArcFromUI = (patch) => {
    setSS((cur) => {
      const sweep = clamp(
        toNum(patch.arcSweepDeg ?? cur.arcSweepDeg ?? 90, 90),
        1, 360
      );
      const rot = toNum(patch.arcRotationDeg ?? cur.arcRotationDeg ?? 0, 0);
      const start = rot;
      const end = rot + sweep;
      return {
        ...cur,
        arcSweepDeg: sweep,
        arcRotationDeg: rot,
        startDeg: start,
        endDeg: end,
      };
    });
  };

  const renderRotationAnimControls = () => {
    if (!["circle", "arc", "square", "triangle", "cone", "line"].includes(emitterShape)) return null;

    return (
      <>
        <div style={sectionHeader}>Rotation (animated)</div>

        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!rotationAnimEnabled}
            onChange={(e) => setSS({ rotationAnimEnabled: e.target.checked })}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>
            Enable animated rotation (adds a live offset to the static rotation)
          </span>
        </label>

        <div style={{ display: rotationAnimEnabled ? "grid" : "none", gap: 10 }}>
          <LabeledSelect
            label="Direction"
            value={rotationAnimDir}
            onChange={(e) => setSS({ rotationAnimDir: e.target.value === "cw" ? "cw" : "ccw" })}
          >
            <option value="ccw">Counter-clockwise</option>
            <option value="cw">Clockwise</option>
          </LabeledSelect>

          <Slider
            label="Speed"
            value={rotationAnimSpeedDegS}
            onChange={(v) => setSS({ rotationAnimSpeedDegS: clamp(v, 0, 1440) })}
            min={0}
            max={1440}
            step={1}
            suffix="deg/s"
          />

          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={!!rotationAffectsParticles}
              onChange={(e) => setSS({ rotationAffectsParticles: e.target.checked })}
              style={{ accentColor: "#64748b" }}
            />
            <span style={{ fontSize: 12 }}>
              Particles move with rotation (inherit tangential motion)
            </span>
          </label>
        </div>
      </>
    );
  };

  const renderShapeSpecificControls = () => {
    if (emitterShape === "beam") return renderBeamControls();

    switch (emitterShape) {
      case "dot":
      default:
        return null;

      case "circle":
        return (
          <>
            <div style={sectionHeader}>Circle</div>
            <Slider
              label="Radius"
              value={radius}
              onChange={(v) => setSS({ radius: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            {renderRotationAnimControls()}
            {renderEmissionControls()}
          </>
        );

      case "arc":
        return (
          <>
            <div style={sectionHeader}>Arc</div>
            <Slider
              label="Radius"
              value={radius}
              onChange={(v) => setSS({ radius: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Sweep"
              value={normalizedSS.arcSweepDeg}
              onChange={(v) => updateArcFromUI({ arcSweepDeg: Math.round(clamp(v, 1, 360)) })}
              min={1}
              max={360}
              step={1}
              suffix="°"
            />
            <Slider
              label="Rotation (base)"
              value={normalizedSS.arcRotationDeg}
              onChange={(v) => updateArcFromUI({ arcRotationDeg: Math.round(clamp(v, DEG_MIN, DEG_MAX)) })}
              min={DEG_MIN}
              max={DEG_MAX}
              step={1}
              suffix="°"
            />
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
              Arc emits along the edge only. Rotation is the starting angle (CCW from +X); Sweep is the span.
            </div>
            {renderRotationAnimControls()}
            {renderEmissionControls()}
          </>
        );

      case "square":
        return (
          <>
            <div style={sectionHeader}>Square</div>
            <Slider
              label="Width"
              value={width}
              onChange={(v) => setSS({ width: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Height"
              value={height}
              onChange={(v) => setSS({ height: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Rotation (base)"
              value={rotationDeg}
              onChange={(v) => setSS({ rotationDeg: v })}
              min={DEG_MIN}
              max={DEG_MAX}
              step={DEG_STEP}
              suffix="°"
            />
            <Slider
              label="Scale"
              value={uiScaleSquare}
              onChange={(v) => {
                setUiScaleSquare(v);
                applyUniformScaleWH(v, "square");
              }}
              min={0.1}
              max={10}
              step={0.05}
              suffix="×"
            />
            {renderRotationAnimControls()}
            {renderEmissionControls()}
          </>
        );

      case "triangle":
        return (
          <>
            <div style={sectionHeader}>Triangle</div>
            <Slider
              label="Base (width)"
              value={width}
              onChange={(v) => setSS({ width: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Height"
              value={height}
              onChange={(v) => setSS({ height: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Rotation (base)"
              value={rotationDeg}
              onChange={(v) => setSS({ rotationDeg: v })}
              min={DEG_MIN}
              max={DEG_MAX}
              step={DEG_STEP}
              suffix="°"
            />
            <Slider
              label="Scale"
              value={uiScaleTriangle}
              onChange={(v) => {
                setUiScaleTriangle(v);
                applyUniformScaleWH(v, "triangle");
              }}
              min={0.1}
              max={10}
              step={0.05}
              suffix="×"
            />
            {renderRotationAnimControls()}
            {renderEmissionControls()}
          </>
        );

      case "cone":
        return (
          <>
            <div style={sectionHeader}>Cone</div>
            <Slider
              label="Length"
              value={radius}
              onChange={(v) => setSS({ radius: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Spread"
              value={spreadDeg}
              onChange={(v) => setSS({ spreadDeg: v })}
              min={SPREAD_MIN}
              max={SPREAD_MAX}
              step={DEG_STEP}
              suffix="°"
            />
            <Slider
              label="Scale"
              value={uiScaleCone}
              onChange={(v) => {
                setUiScaleCone(v);
                applyUniformScaleR(v);
              }}
              min={0.1}
              max={10}
              step={0.05}
              suffix="×"
            />
            {renderSpawnSourceControls(false)}
            {renderRotationAnimControls()}
          </>
        );

      case "line":
        return (
          <>
            <div style={sectionHeader}>Line</div>
            <Slider
              label="Length"
              value={width}
              onChange={(v) => setSS({ width: v })}
              min={METERS_MIN}
              max={METERS_MAX}
              step={METERS_STEP_FINE}
              suffix="m"
            />
            <Slider
              label="Direction (base)"
              value={rotationDeg}
              onChange={(v) => setSS({ rotationDeg: v })}
              min={DEG_MIN}
              max={DEG_MAX}
              step={DEG_STEP}
              suffix="°"
            />
            {renderSpawnSourceControls(false)}
            {renderRotationAnimControls()}
          </>
        );

      case "random":
        return null;
    }
  };

  const [uiFireMode, setUiFireMode] = useState(() => {
    try { return (localStorage.getItem("EmitterTool:UI:fireMode") || "infinite").toLowerCase(); } catch { return "infinite"; }
  });
  const [ruleStyle, setRuleStyle] = useState(() => {
    try { return (localStorage.getItem("EmitterTool:UI:ruleStyle") || "infinite").toLowerCase(); } catch { return "infinite"; }
  });
  const [ruleDur, setRuleDur] = useState(() => {
    try { return Math.max(0.05, toNum(localStorage.getItem("EmitterTool:UI:ruleDur"), 2)); } catch { return 2; }
  });

  useEffect(() => {
    const onModeChange = () => {
      try { setUiFireMode((localStorage.getItem("EmitterTool:UI:fireMode") || "infinite").toLowerCase()); } catch {}
    };
    window.addEventListener("EmitterTool:ModeChange", onModeChange);
    return () => window.removeEventListener("EmitterTool:ModeChange", onModeChange);
  }, []);

  useEffect(() => {
    const onRuleCfg = (ev) => {
      const detail = ev && ev.detail ? ev.detail : {};
      if (detail.ruleStyle) {
        setRuleStyle(String(detail.ruleStyle).toLowerCase());
      }
      if (typeof detail.ruleDurationS === "number") {
        const d = Math.max(0.05, detail.ruleDurationS);
        setRuleDur(d);
      }
    };
    window.addEventListener("EmitterTool:RuleConfig", onRuleCfg);
    return () => window.removeEventListener("EmitterTool:RuleConfig", onRuleCfg);
  }, []);

  const saveRuleCfg = (style, dur) => {
    try { localStorage.setItem("EmitterTool:UI:ruleStyle", style); } catch {}
    try { localStorage.setItem("EmitterTool:UI:ruleDur", String(dur)); } catch {}
    emit("EmitterTool:RuleConfig", { ruleStyle: style, ruleDurationS: dur });
  };

  const renderRuleControls = () => {
    if (uiFireMode !== "rule") return null;
    return (
      <>
        <div style={sectionHeader}>Rule runtime</div>
        <LabeledSelect
          label="Rule spawner"
          value={ruleStyle}
          onChange={(e) => {
            const v = (e.target.value || "infinite").toLowerCase();
            setRuleStyle(v);
            saveRuleCfg(v, ruleDur);
          }}
        >
          <option value="infinite">Infinite (keep spawning)</option>
          <option value="timed">Timed (then drain)</option>
        </LabeledSelect>

        {ruleStyle === "timed" && (
          <Slider
            label="Duration"
            value={ruleDur}
            onChange={(v) => {
              const d = Math.max(0.05, v);
              setRuleDur(d);
              saveRuleCfg(ruleStyle, d);
            }}
            min={0.05}
            max={120}
            step={0.05}
            suffix="s"
          />
        )}
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          Timed mode: emit for the duration, then stop creating new particles and let the live ones die naturally.
        </div>
      </>
    );
  };

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ maxWidth: "100%", overflowX: "hidden", display: "grid", gap: 10 }}>
        <div style={sectionHeader}>Shape</div>

        <LabeledSelect
          label="Emitter Shape"
          value={emitterShape || "dot"}
          onChange={(e) => {
            setEmitterShape?.(e.target.value);
          }}
        >
          {SHAPES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </LabeledSelect>

        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!showShape}
            onChange={(e) => {
              setShowShape?.(e.target.checked);
            }}
            style={{ accentColor: "#64748b" }}
          />
          <span style={{ fontSize: 12 }}>Show emitter shape</span>
        </label>

        {renderShapeSpecificControls()}

        {renderRuleControls()}
      </div>
    </CollapsibleBox>
  );
}

export { EmitterPanel };
